/**
 * <sticky-header> — sections/header.liquid
 *
 * - Sets --header-height (header only) and --announcement-height on :root from measured heights.
 * - Toggles .header--scrolled on itself past 40px.
 * - Transparent header (.header--transparent, rendered server-side on the home page): mirrors it as
 *   .section-header--overlay on the section wrapper and .header--overlay on <body> (CSS fallback for
 *   browsers without :has(), and a hook for other sections). Adds .header--open while the mobile
 *   drawer or the search panel is open so the header turns solid under them.
 * - data-sticky-type="always" | "on-scroll-up" | "none". Sticky positioning lives on the section
 *   wrapper (.section-header--sticky); "on-scroll-up" adds/removes .header--hidden on it.
 *   Hiding is suppressed while a menu, the drawer, the search panel or a localization
 *   picker is open (Dawn's header-menu / localization-form set `preventHide` on .header-wrapper).
 * - Opens desktop <header-menu> dropdowns on hover (pointer devices only); keyboard uses the
 *   native <details> toggle handled by details-disclosure.js.
 * - Closes the mobile drawer when its overlay is clicked (Dawn's MenuDrawer has no overlay).
 */
if (!customElements.get('sticky-header')) {
  customElements.define(
    'sticky-header',
    class StickyHeader extends HTMLElement {
      constructor() {
        super();
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onSectionLoad = this.onSectionLoad.bind(this);
        this.preventHide = false;
        this.lastScrollTop = 0;
        this.ticking = false;
      }

      connectedCallback() {
        this.section = this.closest('.shopify-section') || this.parentElement;
        this.stickyType = this.dataset.stickyType || 'always';
        this.section.classList.toggle('section-header--sticky', this.stickyType !== 'none');

        this.transparent = this.classList.contains('header--transparent');
        this.section.classList.toggle('section-header--overlay', this.transparent);
        document.body.classList.toggle('header--overlay', this.transparent);

        this.measure();
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this);
        this.observeAnnouncement();

        window.addEventListener('scroll', this.onScroll, { passive: true });
        document.addEventListener('shopify:section:load', this.onSectionLoad);
        this.onScroll();

        this.bindHoverMenus();
        this.bindDrawerOverlay();
        this.bindOpenState();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.section.classList.remove('section-header--overlay');
        document.body.classList.remove('header--overlay');
      }

      /* ---------- measurements ---------- */

      observeAnnouncement() {
        const bar = document.querySelector('.section-announcement-bar');
        if (bar && bar !== this.announcement) {
          if (this.announcement) this.resizeObserver.unobserve(this.announcement);
          this.announcement = bar;
          this.resizeObserver.observe(bar);
        }
      }

      onResize() {
        this.measure();
      }

      onSectionLoad() {
        // The announcement bar may have been re-rendered by the theme editor.
        this.observeAnnouncement();
        this.measure();
      }

      measure() {
        const root = document.documentElement.style;
        const headerHeight = Math.round(this.getBoundingClientRect().height);
        if (headerHeight > 0) root.setProperty('--header-height', `${headerHeight}px`);

        const bar = document.querySelector('.section-announcement-bar');
        const barHeight = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
        root.setProperty('--announcement-height', `${barHeight}px`);
        this.hideThreshold = headerHeight + barHeight;
      }

      /* ---------- scroll state ---------- */

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.update(Math.max(window.scrollY || 0, 0));
          this.ticking = false;
        });
      }

      update(scrollTop) {
        this.classList.toggle('header--scrolled', scrollTop > 40);

        if (this.stickyType === 'on-scroll-up') {
          const scrollingDown = scrollTop > this.lastScrollTop;
          const pastHeader = scrollTop > (this.hideThreshold || 0);
          const locked = this.preventHide || this.querySelector('details[open]') !== null;

          if (scrollingDown && pastHeader && !locked) {
            this.section.classList.add('header--hidden');
          } else if (!scrollingDown || !pastHeader) {
            this.section.classList.remove('header--hidden');
          }
        }

        this.lastScrollTop = scrollTop;
      }

      /* ---------- desktop hover menus ---------- */

      bindHoverMenus() {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

        this.querySelectorAll('header-menu').forEach((menu) => {
          const details = menu.querySelector('details');
          const summary = details && details.querySelector('summary');
          if (!details || !summary) return;
          let closeTimer;

          const setOpen = (open) => {
            details.open = open;
            summary.setAttribute('aria-expanded', String(open));
          };

          menu.addEventListener('mouseenter', () => {
            window.clearTimeout(closeTimer);
            if (!details.open) setOpen(true);
          });

          menu.addEventListener('mouseleave', () => {
            closeTimer = window.setTimeout(() => {
              if (!menu.contains(document.activeElement)) setOpen(false);
            }, 160);
          });

          // Keep aria-expanded honest when details-disclosure.js or Escape closes it.
          details.addEventListener('toggle', () => {
            summary.setAttribute('aria-expanded', String(details.open));
          });
        });
      }

      /* ---------- open state (drawer / search) ---------- */

      bindOpenState() {
        const drawer = this.querySelector('.menu-drawer-container');
        const search = this.querySelector('.header__search details');
        const panels = [drawer, search].filter(Boolean);
        if (panels.length === 0) return;

        const sync = () => {
          this.classList.toggle(
            'header--open',
            panels.some((details) => details.open)
          );
        };

        panels.forEach((details) => details.addEventListener('toggle', sync));
        sync();
      }

      /* ---------- mobile drawer overlay ---------- */

      bindDrawerOverlay() {
        const drawer = this.querySelector('header-drawer');
        const overlay = this.querySelector('.menu-drawer__overlay');
        if (!drawer || !overlay) return;

        overlay.addEventListener('click', (event) => {
          const summary = drawer.querySelector('summary');
          if (typeof drawer.closeMenuDrawer === 'function') {
            drawer.closeMenuDrawer(event, summary);
          } else {
            drawer.querySelector('details').removeAttribute('open');
          }
          summary.setAttribute('aria-expanded', 'false');
        });
      }
    }
  );
}
